import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import React, { useEffect, useState } from 'react';

interface SearchBarCompProps {
  autoCompleteDataTestId: string;
  defaultValue?: string;
  disabled?: boolean;
  error?: boolean;
  exception: boolean;
  getOptionLabel?: (option: any) => string;
  helperText?: string;
  inputValue?: string;
  onClear?: () => void;
  onFocus?: () => void;
  onInputChange: (
    value: string,
    event?: React.SyntheticEvent,
    reason?: AutocompleteInputChangeReason
  ) => void;
  onKeyDown?: () => void;
  onOptionSelected: (value: string) => void;
  options: any;
  placeHolder: string;
  renderOption?: (option: any) => React.ReactNode;
  textFieldDataTestId: string;
}

export default function SearchBarComp(props: Readonly<SearchBarCompProps>) {
  const [inputValue, setInputValue] = useState<string>('');
  const handleInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setInputValue(value);
    props.onInputChange(value, event, reason);
  };
  const handleOnChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption') props.onOptionSelected(value ?? '');
    if (reason === 'clear' && props.onClear) props.onClear();
  };
  const handleKeyDown = (event: any) => {
    if (event.target.value)
      if (event.keyCode === 13) {
        event.preventDefault();
        if (props.onKeyDown) props.onKeyDown();
        else props.onOptionSelected(event.target.defaultValue);
      }
  };
  useEffect(() => {
    if (props.defaultValue) setInputValue(props.defaultValue);
    else setInputValue('');
  }, [props.defaultValue]);
  return (
    <Autocomplete
      data-testid={props.autoCompleteDataTestId}
      disablePortal
      disabled={props.disabled}
      filterOptions={(options) => options}
      freeSolo
      getOptionLabel={props.getOptionLabel}
      inputValue={inputValue}
      onChange={handleOnChange}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      options={props.options}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: !props.exception ? (
              <InputAdornment position="start">
                <IconButton>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          data-testid={props.textFieldDataTestId}
          error={props.error}
          fullWidth
          helperText={props.helperText}
          onFocus={props.onFocus}
          onKeyDown={handleKeyDown}
          placeholder={props.placeHolder}
        />
      )}
      renderOption={(liProps: any, option: any) => (
        <li {...liProps} key={option.id}>
          {props.renderOption ? props.renderOption(option) : option}
        </li>
      )}
      sx={{
        '& .MuiTextField-root .MuiOutlinedInput-root': {
          borderRadius: '4px',
        },
        '.MuiOutlinedInput-root:has(.MuiInputAdornment-positionStart)': {
          padding: '0px 8px !important',
        },
      }}
    />
  );
}
